import { useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTRPCClient } from "@/trpc";
import { RevealProviderDialog } from "./reveal-provider-dialog";

type RevealProviderButtonProps = {
  providerUserId: string;
  organizationBusinessUnitId: string;
  onRevealed?: () => void;
  size?: "sm" | "default";
  variant?: "outline" | "default" | "ghost";
};

export function RevealProviderButton({
  providerUserId,
  organizationBusinessUnitId,
  onRevealed,
  size = "sm",
  variant = "outline",
}: RevealProviderButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [initialMonthlyRevealLimit, setInitialMonthlyRevealLimit] = useState<
    number | null
  >(null);
  const trpcClient = useTRPCClient();

  const handleRevealClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isCheckingLimit) {
      return;
    }

    setIsCheckingLimit(true);
    try {
      const availability =
        await trpcClient.organization.getProviderRevealAvailability.query({
          organizationBusinessUnitId,
        });
      setInitialMonthlyRevealLimit(
        availability.limitReached ? availability.monthlyLimit : null,
      );
    } catch {
      // The reveal mutation remains the authoritative limit check, so allow
      // the normal confirmation to open if this preflight request fails.
      setInitialMonthlyRevealLimit(null);
    } finally {
      setIsCheckingLimit(false);
      setDialogOpen(true);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        disabled={isCheckingLimit}
        className="flex items-center gap-2"
        onClick={handleRevealClick}
      >
        {isCheckingLimit ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Eye className="h-3.5 w-3.5" />
        )}
        <span>Reveal</span>
      </Button>

      <RevealProviderDialog
        open={dialogOpen}
        onOpenChange={(nextOpen) => {
          setDialogOpen(nextOpen);
          if (!nextOpen) {
            setInitialMonthlyRevealLimit(null);
          }
        }}
        providerUserId={providerUserId}
        organizationBusinessUnitId={organizationBusinessUnitId}
        initialMonthlyRevealLimit={initialMonthlyRevealLimit}
        onRevealed={onRevealed}
      />
    </>
  );
}
